import React from 'react';
import { SetupEntryGate } from 'features/SetupEntry';
import styles from './styles.module.scss';
import { GuideSlide } from 'entities/Guide';
import {
  Arrow,
  Button,
  Copy,
  Download,
  Flex,
  QueryStatusBoundary,
  Typography,
} from 'shared/_ui';
import { useTranslation } from 'react-i18next';
import { useSliderContext } from '../../model';
import classNames from 'classnames';
import { useFetchUserInfo, useFetchXrayConnections } from 'entities/Connection';
import { useFetchCountries } from 'entities/Country';
import { usePrevious } from '@uidotdev/usehooks';
import { useCreateXrayConnection } from 'features/CreateXrayConnection';
import { notify, useMatchMedia } from 'shared';
import { useStoreUiControls } from 'shared/lib';
import { CONTROL_IDS } from 'shared/consts';
import { SlideProps } from 'entities/Guide';
import { Instruction } from './ui/Instruction';
import { useConnectionDevices } from 'entities/Device';

const InstructionSlide: React.FC<SlideProps> = ({
  className,
  isActive,
  number,
}) => {
  const { t } = useTranslation();

  const isCreated = React.useRef<boolean>(false);
  const [createdConnectionId, setCreatedConnectionId] =
    React.useState<number>();
  const [createXray, { isError: isCreateXrayError }] =
    useCreateXrayConnection();
  const { isLoading: isFetchUserInfoLoading, data: userInfo } =
    useFetchUserInfo(null);
  const { isLoading: isFetchCountriesLoading, data: countries } =
    useFetchCountries(null);
  const { data: xrayConnections } = useFetchXrayConnections(null);
  const { setIsOpen } = useStoreUiControls(CONTROL_IDS.GUIDE);

  const prevXrayConnections = usePrevious(xrayConnections);

  const { isMobile } = useMatchMedia();

  const {
    device,
    chosenServerId,
    installationType,
    setXrayConnectionLink,
    xrayConnectionLink,
    next,
    prev,
    isConnectionCreate,
  } = useSliderContext();

  const { createDevice } = useConnectionDevices();

  const createXrayConnection = async () => {
    const firstCountry = countries?.[0];
    if (!firstCountry) return;
    try {
      const result = await createXray(
        chosenServerId || firstCountry.id
      ).unwrap();

      if (result) {
        setXrayConnectionLink(result.subscription);
        setCreatedConnectionId(result.id);
      }
    } catch (error) {
      notify.error(t('xray.createError'));
    }

    isCreated.current = true;
  };

  React.useEffect(() => {
    if (
      !userInfo ||
      !countries?.length ||
      (installationType !== 'app' && isCreated.current)
    )
      return;

    const isLimit =
      (xrayConnections?.length || 0) >= userInfo?.tariff.numberOfAccounts;

    if (isConnectionCreate && isActive && !isLimit) {
      switch (installationType) {
        case 'app': {
          if (
            userInfo.pricingRegion !== 'RU' && xrayConnections?.some(
              (connection) => connection.serverId === chosenServerId
            )
          ) {
            notify.info(t('xray.duplicateConnection'));
            setTimeout(() => setIsOpen(false), 1000);

            break;
          }
          createXrayConnection();
          break;
        }
      }
    }
  }, [isConnectionCreate, isActive, userInfo, countries]);

  React.useEffect(() => {
    if (!prevXrayConnections || !xrayConnections) return;

    const lastConnection = xrayConnections.at(-1);

    if (!lastConnection) return;

    if (prevXrayConnections.length < xrayConnections.length) {
      createDevice(lastConnection.id, device);
    }
  }, [prevXrayConnections, xrayConnections, device]);

  const instructions = (
    <GuideSlide
      className={classNames(className, styles.slide)}
      head={
        <Flex direction="column" spacing={16}>
          <Flex spacing={20}>
            <Flex className={styles.number} align="center" justify="center">
              <Typography.Text size={6}>{number}</Typography.Text>
            </Flex>
            <Typography.Title level={2}>
              {t(`guide.${installationType}.title`)}
            </Typography.Title>
          </Flex>
        </Flex>
      }
      body={<></>}
      controls={
        <Flex justify="space-between" spacing={10}>
          <Button
            onClick={prev}
            className={styles.back}
            type="secondary"
            prefix={<Arrow className={styles.arrow} />}
          >
            {t('shared.text.back')}
          </Button>
          <Button
            onClick={() => {
              next();
            }}
            className={styles.next}
            type="primary"
          >
            {t('shared.text.finish')}!
          </Button>
        </Flex>
      }
      footer={
        <Flex direction="column" spacing={36}>
          {installationType === 'app' && (
            <Flex direction={isMobile ? 'column' : 'row'} spacing={16}>
              {device === 'windows' && (
                <Flex spacing={16} direction="column">
                  <Button
                    size="large"
                    className={styles.button}
                    prefix={<Download className={styles.download} />}
                    type="secondary"
                    onClick={() =>
                      window.open(
                        'https://github.com/hiddify/HiddifyN/releases/download/v3.0.0/win-x64.zip'
                      )
                    }
                  >
                    {t('guide.app.hiddifynext')}
                  </Button>
                  <Button
                    size="large"
                    className={styles.button}
                    prefix={<Download className={styles.download} />}
                    type="secondary"
                    onClick={() =>
                      window.open(
                        'https://www.microsoft.com/ru-tm/store/b/home'
                      )
                    }
                  >
                    {t('guide.app.microsoft')}
                  </Button>
                </Flex>
              )}
              {device === 'linux' && (
                <>
                  <Button
                    size="large"
                    className={styles.button}
                    prefix={<Download className={styles.download} />}
                    type="secondary"
                    onClick={() =>
                      window.open(
                        'https://github.com/hiddify/hiddify-app/releases/latest/download/Hiddify-Linux-x64.AppImage'
                      )
                    }
                  >
                    {t('guide.app.hiddifynext')}
                  </Button>
                </>
              )}
              {device === 'mac' && (
                <>
                  <Button
                    size="large"
                    className={styles.button}
                    prefix={<Download className={styles.download} />}
                    type="secondary"
                    onClick={() => {
                      window.open(
                        'https://apps.apple.com/us/app/foxray/id6448898396'
                      );
                    }}
                  >
                    {t('guide.app.foxray')}
                  </Button>
                </>
              )}
              {device === 'iphone' && (
                <>
                  <Button
                    size="large"
                    className={styles.button}
                    prefix={<Download className={styles.download} />}
                    type="secondary"
                    onClick={() => {
                      window.open(
                        'https://apps.apple.com/us/app/foxray/id644889839'
                      );
                    }}
                  >
                    {t('guide.app.foxray')}
                  </Button>
                </>
              )}
              {device === 'android' && (
                <Flex spacing={16} direction="column">
                  <Button
                    size="large"
                    className={styles.button}
                    prefix={<Download className={styles.download} />}
                    type="secondary"
                    onClick={() =>
                      window.open(
                        'https://play.google.com/store/apps/details?id=app.hiddify.com&hl=en_US'
                      )
                    }
                  >
                    {t('guide.app.hiddifynext')}
                  </Button>
                  <Button
                    size="large"
                    className={styles.button}
                    prefix={<Download className={styles.download} />}
                    type="secondary"
                    onClick={() =>
                      window.open(
                        'https://github.com/hiddify/HiddifyNG/releases/download/v6.0.4/HiddifyNG.apk'
                      )
                    }
                  >
                    {`${t('guide.app.hiddifynext')} Huawei`}
                  </Button>
                  <Button
                    size="large"
                    className={styles.button}
                    prefix={<Download className={styles.download} />}
                    type="secondary"
                    onClick={() =>
                      window.open(
                        'https://www.samsung.com/ru/apps/galaxy-store/'
                      )
                    }
                  >
                    {`${t('guide.app.samsung')}`}
                  </Button>
                </Flex>
              )}

              {xrayConnectionLink && (
                <Copy
                  className={styles.link}
                  displayText={t('guide.app.linkKey')}
                  truncate
                  variant="filled"
                  text={xrayConnectionLink}
                />
              )}
            </Flex>
          )}
          <Typography.Text type="secondary" size={4}>
            {t('guide.stepByStep')}:
          </Typography.Text>
          <Instruction
            device={device}
            installationType={installationType}
            copyLink={
              <QueryStatusBoundary
                isLoading={isFetchUserInfoLoading || isFetchCountriesLoading}
                isError={isCreateXrayError}
                errorMessage={t('connection.error.create')}
              >
                {xrayConnectionLink && (
                  <div style={{ marginTop: 12 }}>
                    <Copy
                      displayText={t('guide.app.linkKey')}
                      truncate
                      variant="filled"
                      text={xrayConnectionLink}
                    />
                  </div>
                )}
              </QueryStatusBoundary>
            }
          />
        </Flex>
      }
    />
  );
  if (isConnectionCreate) {
    return createdConnectionId ? (
      <SetupEntryGate connectionId={createdConnectionId}>
        {instructions}
      </SetupEntryGate>
    ) : (
      <QueryStatusBoundary
        isLoading={!isCreateXrayError}
        isError={isCreateXrayError}
        errorMessage={t('connection.error.create')}
      >
        <p role="status">{t('setup.loading')}</p>
      </QueryStatusBoundary>
    );
  }
  return instructions;
};

export { InstructionSlide };
